// ============================================================
// Trade Like a Woman — The Foundation
// App.js — COMPLETE INTEGRATED APPLICATION
//
// Deploy this as sovereign-academy/src/App.js
//
// All components are inlined here so only this single file
// is needed (plus package.json, index.js, index.html).
//
// Student flow:
//   1. Disclaimer gate (10 checkboxes + e-signature)
//   2. Lynn's Introduction Letter (scroll-gated)
//   3. Progress Dashboard (home)
//   4. Chapters 01–09 + 3B (gated at 75% quiz score)
//   5. Practice tools: Trade Builder, Chain Simulator,
//      Stock Selection, Scenario Library, Glossary,
//      Candlestick Simulator, P&L Tracker, Journal
//   6. Readiness Test → Trading Room eligibility
// ============================================================

// TO RENAME VERCEL PROJECT:
// 1. Go to vercel.com → your project settings
// 2. Under General → Project Name → rename to trade-like-a-woman
// 3. Under Domains → Add Domain → tradelikeawoman.com
// 4. Follow DNS instructions from your domain registrar
// This does not require any code changes.

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import CandlestickSimulator from './CandlestickSimulator';
import MovingAverageSimulator from './MovingAverageSimulator';
import SPXConfluenceLive, { C, useSignals, MiniChart, GaugeMeter, SignalRow } from './components/SPXConfluenceLive';
import { V, serif, mono, eyebrow, card, btnGold, btnGhost, xpToLevel, TIER2_SESSION_CODE } from './theme';
import { applyTooltips, Tooltip, DEFINITIONS } from './utils/tooltips';
import { CHAPTERS, CHAPTER_CONTENT, CHAPTER_FEEDBACK_TITLES, CHAPTER_BOTTOM_NAV, CHAPTER_KEY_TERMS } from './data/chapters';
import FeedbackCard, { KeyTermPill, KeyTermsCard } from './components/FeedbackCard';
import ChapterViewer from './components/ChapterViewer';
import ProgressDashboard from './components/ProgressDashboard';
import StrategyInfoTooltip from './components/StrategyInfoTooltip';
import TradeBuilderWizard from './components/TradeBuilder';
import PLTracker from './components/PLTracker';
import RiskDashboard from './components/RiskDashboard';
import TradingJournal from './components/TradingJournal';
import MiniPayoff from './components/MiniPayoff';
import { CHAPTER_VISUALS } from './components/ChapterVisuals';
import OptionsChainSimulator from './components/OptionsChainSimulator';
import ReadingTheMarketView from './components/ReadingTheMarketView';
import CSPSimulator from './components/CSPSimulatorV2';
import FullTradeSimulator from './components/FullTradeSimulator';
import Ch0View from './components/Ch0View';
import ReadinessTestView from './components/ReadinessTestView';
import SupportResistancePracticeView from './components/SupportResistancePracticeView';
import ConfigView, { CandleSimWrapper, MASimWrapper } from './components/ConfigView';
import Sidebar from './components/Sidebar';
import SpreadBuilderPracticeView from './components/SpreadBuilderPracticeView';
import SPXConfluence from './components/SPXConfluence';
import LynnIntroLetter from './LynnIntroLetter';
import ScenarioLibrary from './ScenarioLibrary';
import StockSelectionChapter from './StockSelectionChapter';
import ConfluencePracticeView, { IndicatorLessonView, PracticeComingSoonView, VWAPPracticeView, RSIPracticeView, GreeksPracticeView, CalendarView, BollingerBandsPracticeView } from './components/PracticeViews';
import { LOCKED_CALCS, DteSimView, CalcHeader, CalcPositionView, CalcOptionsProfitView, CalcBreakevenView, CalcRRView, CalcCompoundView, LockedCalcView, CalculatorsLandingView, MeditationsView } from './components/CalculatorViews';
import AdminDashboard, { AdminFeedbackView, AuthScreen } from './components/AdminViews';
import LandingPage from './components/LandingPage';
import FirstPaperTrade from './components/FirstPaperTrade';
import PracticeTheGreeks from './components/PracticeTheGreeks';
import RollCalculatorView from './components/RollCalculatorView';
import { TradingRulesView, ManualView, TradeSetupView } from './components/TradingRulesView';
import GlossaryView, { AppErrorBoundary, RiskManagementView } from './components/GlossaryView';
import InteractiveStrategyCharts from './InteractiveStrategyCharts';
import ToolsHub from './ToolsHub';
import InnerTrader from './InnerTrader';
import MarketBriefView from './MarketBrief';
import { supabase } from './supabaseClient';
import { migrateLocalDataToSupabase, loadDataFromSupabase } from './dataSync';

// ── Google Fonts loader ───────────────────────────────────────
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=DM+Mono:wght@400;500&family=Jost:wght@300;400;500&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { max-width: 100vw; overflow-x: hidden; }
    body { background: #0a0a0c; color: #f0ede6; font-family: 'Jost', sans-serif; font-variant-numeric: slashed-zero; }
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: #0a0a0c; }
    ::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.3); border-radius: 2px; }
    button { font-family: 'Jost', sans-serif; }
    input[type="number"], input[type="text"] { font-variant-numeric: slashed-zero; }
    .num { font-family: 'DM Mono', monospace; font-variant-numeric: slashed-zero; }
    @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.35;transform:scale(0.7)} }
  `}</style>
);


// ── Text-to-speech hook ───────────────────────────────────────
function useSpeech() {
  const [speaking, setSpeaking] = useState(false);
  const [enabled, setEnabled] = useState(false);

  const speak = useCallback((text) => {
    if (!enabled || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v =>
      v.name.includes('Samantha') ||
      v.name.includes('Karen') ||
      v.name.includes('Victoria') ||
      v.name.includes('Female') ||
      v.lang === 'en-US'
    );
    if (preferred) utt.voice = preferred;
    utt.rate  = 0.92;
    utt.pitch = 1.0;
    utt.volume = 1.0;
    utt.onstart = () => setSpeaking(true);
    utt.onend   = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, [enabled]);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, []);

  return { speak, stop, speaking, enabled, setEnabled };
}

// ── Initial app state ─────────────────────────────────────────
const INITIAL_STATE = {
  // Gates
  disclaimerSigned:     false,
  disclaimerName:       '',
  disclaimerDate:       '',
  introLetterRead:      false,

  // Student info
  studentName:          localStorage.getItem('tlaw_student_name') || '',

  // Chapters
  completedChapters:    [],
  chapterScores:        {},
  activeChapter:        'ch01',

  // Practice tools
  paperTradeDays:       0,
  journalEntries:       [],
  tradeBuilderCount:    0,
  tradeBuilderScores:   [],
  simulatorSessions:    0,
  simulatorCorrect:     0,
  simulatorTotal:       0,
  stockSelectionDone:   false,
  stockSelectionScore:  0,
  scenariosCompleted:   [],   // array of { id, correct }
  glossaryXP:           0,

  // Readiness
  readinessPassed:      false,
  readinessScore:       0,
  readinessDate:        null,

  // P&L
  plTrades:             [],

  // Inner Work
  innerWorkCheckins:    [],
  innerWorkReflections: [],
  innerWorkPrompts:     'always',

  // XP
  xpTotal:              0,

  // Dashboard
  whys:             [],
  financialGoals:   [],
  accountSize:      0,
  isGraduated:      false,
  tradingMode:      'practice',
  currentBalance:   0,
  accountType:      'cash',
  weeklyMaxLoss:    0,
  adminMode:        false,
  tier2Unlocked:    false,
  arrivedFromGreeks: false,
  simulatorEntrySource: 'sidebar',
  chapter0Checklist:{},
  trades:           [],
  favoriteRules:    [],
  customRules:      [],

  // Smart Return — universal back button
  practiceReturnView: null,
  practiceReturnTab:  null,
};

const PAPER_TRADE_GOAL = 30;

// ═══════════════════════════════════════════════════════════════
// SECTION 1 — DISCLAIMER GATE
// ═══════════════════════════════════════════════════════════════
const DISCLAIMER_ITEMS = [
  'I understand that options trading involves substantial risk of loss and is not suitable for all investors.',
  'I acknowledge that past performance shown or discussed is not indicative of future results.',
  'I understand that Trade Like a Woman is an educational program, not a licensed investment advisory service.',
  'I acknowledge that Lynn is not a licensed financial advisor, broker, or registered investment advisor.',
  'I understand that paper trading and real trading carry different psychological and financial risks.',
  'I agree that I will complete the full curriculum before trading with real money.',
  'I acknowledge that I must complete at least 30 days of paper trading before trading real capital.',
  'I understand that no specific returns are promised or guaranteed by this program.',
  'I agree to take full personal responsibility for all trading decisions I make.',
  'I have read and agree to abide by the Trade Like a Woman community guidelines and code of conduct.',
];

function DisclaimerGate({ onComplete }) {
  const [checked, setChecked] = useState({});
  const [name, setName]       = useState('');
  const [agreed, setAgreed]   = useState(false);
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const allChecked = DISCLAIMER_ITEMS.every((_, i) => checked[i]) && name.trim().length > 2 && agreed;

  return (
    <div style={{ background: V.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 20px 60px' }}>
      <div style={{ maxWidth: '680px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{ fontSize: '32px', color: V.gold, marginBottom: '10px' }}>♀</div>
          <h1 style={{ ...serif, fontSize: '32px', fontWeight: 500, marginBottom: '8px' }}>Trade Like a Woman</h1>
          <p style={{ ...serif, fontSize: '20px', color: V.muted, fontStyle: 'italic' }}>The Foundation</p>
        </div>

        <div style={card}>
          <div style={{ ...eyebrow, marginBottom: '16px' }}>Risk Disclosure & Program Agreement</div>
          <p style={{ fontSize: '13px', color: V.muted, lineHeight: 1.8, marginBottom: '20px' }}>
            Before accessing the program, please read and individually acknowledge each of the following statements. This is not a formality — it's the foundation of a responsible trading education.
          </p>
          {DISCLAIMER_ITEMS.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: `0.5px solid rgba(240,237,230,0.05)`, cursor: 'pointer', alignItems: 'flex-start' }}
              onClick={() => setChecked(c => ({ ...c, [i]: !c[i] }))}>
              <div style={{ width: '18px', height: '18px', borderRadius: '3px', border: `1px solid ${checked[i] ? V.sage : 'rgba(240,237,230,0.2)'}`, background: checked[i] ? 'rgba(122,171,138,0.15)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px', fontSize: '11px', color: V.sage }}>
                {checked[i] ? '✓' : ''}
              </div>
              <span style={{ fontSize: '13px', color: checked[i] ? V.text : V.muted, lineHeight: 1.6 }}>{item}</span>
            </div>
          ))}
        </div>

        <div style={card}>
          <div style={{ ...eyebrow, marginBottom: '16px' }}>Confidentiality</div>
          <p style={{ fontSize: '13px', color: V.muted, lineHeight: 1.8, marginBottom: '16px' }}>
            The curriculum strategies frameworks tools simulators methodologies and all platform content represent proprietary educational material developed by Lynn Gormley and Trade Like a Woman™.
          </p>
          <p style={{ fontSize: '13px', color: V.muted, lineHeight: 1.8, marginBottom: '12px' }}>By accessing this platform you agree:</p>
          {[
            'Not to share, reproduce, or distribute any platform content including curriculum, lessons, strategies, frameworks, or tools without express written permission.',
            'Not to screenshot, record, or copy platform content for distribution to third parties.',
            'Not to share your login credentials or allow others to access your account.',
            'To keep all platform content confidential during and after your participation in the program.',
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', padding: '6px 0', alignItems: 'flex-start' }}>
              <span style={{ color: V.gold, fontSize: '13px', flexShrink: 0, marginTop: '2px' }}>—</span>
              <span style={{ fontSize: '13px', color: V.muted, lineHeight: 1.6 }}>{item}</span>
            </div>
          ))}
          <p style={{ fontSize: '13px', color: V.muted, lineHeight: 1.8, marginTop: '14px' }}>
            This confidentiality obligation survives termination of your access to the platform.
          </p>
        </div>

        <div style={card}>
          <div style={{ ...eyebrow, marginBottom: '16px' }}>Non-Compete and Non-Solicitation</div>
          <p style={{ fontSize: '13px', color: V.muted, lineHeight: 1.8, marginBottom: '12px' }}>
            By accessing this platform you agree that for a period of 24 months following your last access to the platform you will not:
          </p>
          {[
            'Create, develop, or sell any options trading education program that substantially replicates the curriculum structure, methodology, or content of Trade Like a Woman™.',
            'Solicit other platform members for competing education programs or services.',
            'Use proprietary frameworks, methodologies, or content from this platform as the basis for competing educational content.',
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', padding: '6px 0', alignItems: 'flex-start' }}>
              <span style={{ color: V.gold, fontSize: '13px', flexShrink: 0, marginTop: '2px' }}>—</span>
              <span style={{ fontSize: '13px', color: V.muted, lineHeight: 1.6 }}>{item}</span>
            </div>
          ))}
          <p style={{ fontSize: '13px', color: V.muted, lineHeight: 1.8, marginTop: '14px' }}>
            This restriction applies to direct competition only and does not prevent you from trading options for your own personal account.
          </p>
        </div>

        <div style={card}>
          <div style={{ ...eyebrow, marginBottom: '14px' }}>Electronic Signature</div>
          <p style={{ fontSize: '13px', color: V.muted, marginBottom: '16px' }}>By typing your full name below, you are providing your electronic signature and agreeing to all statements above.</p>
          <input
            type="text" placeholder="Type your full name to sign"
            value={name} onChange={e => setName(e.target.value)}
            style={{ width: '100%', padding: '12px 14px', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.text, fontSize: '14px', marginBottom: '10px', outline: 'none' }}
          />
          <p style={{ fontSize: '12px', color: V.dim }}>Date: {today}</p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '14px', alignItems: 'flex-start', cursor: 'pointer' }}
            onClick={() => setAgreed(a => !a)}>
            <div style={{ width: '18px', height: '18px', borderRadius: '3px', border: `1px solid ${agreed ? V.sage : 'rgba(240,237,230,0.2)'}`, background: agreed ? 'rgba(122,171,138,0.15)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px', fontSize: '11px', color: V.sage }}>
              {agreed ? '✓' : ''}
            </div>
            <span style={{ fontSize: '13px', color: V.muted, lineHeight: 1.6 }}>I confirm that the name entered above is my own and constitutes my legally binding electronic signature on this agreement.</span>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '10px', fontSize: '12px', color: V.dim }}>
          {Object.values(checked).filter(Boolean).length} / {DISCLAIMER_ITEMS.length} statements acknowledged
        </div>
        <button style={{ ...btnGold, width: '100%', padding: '16px', fontSize: '14px', opacity: allChecked ? 1 : 0.35, cursor: allChecked ? 'pointer' : 'not-allowed' }}
          disabled={!allChecked}
          onClick={() => allChecked && onComplete(name)}>
          I Agree — Enter The Foundation →
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// NAME GATE — first-time name capture
// ═══════════════════════════════════════════════════════════════
function NameGateView({ onComplete }) {
  const [nameInput, setNameInput] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) { setError(true); return; }
    setError(false);
    onComplete(trimmed);
  };

  return (
    <div style={{ background: '#0a0a0c', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: '#111114', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '16px', padding: '48px 40px', maxWidth: '440px', width: '90%', textAlign: 'center' }}>
        <div style={{ fontSize: '36px', color: V.gold, marginBottom: '8px' }}>♀</div>
        <h1 style={{ ...serif, fontSize: '28px', fontWeight: 500, color: V.gold, margin: '0 0 6px' }}>Welcome to Trade Like a Woman</h1>
        <p style={{ ...serif, fontSize: '16px', color: V.muted, fontStyle: 'italic', margin: '0 0 24px' }}>The Foundation</p>
        <div style={{ borderTop: `0.5px solid rgba(240,237,230,0.08)`, marginBottom: '24px' }} />
        <label style={{ display: 'block', fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>
          What is your first name?
        </label>
        <input
          type="text"
          placeholder="Your first name"
          value={nameInput}
          onChange={e => { setNameInput(e.target.value); setError(false); }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          autoFocus
          style={{ width: '100%', boxSizing: 'border-box', background: '#18181c', border: `1px solid ${error ? V.rose : 'rgba(201,168,76,0.2)'}`, borderRadius: '8px', padding: '12px 14px', color: V.text, fontSize: '14px', fontFamily: "'Jost',sans-serif", textAlign: 'center', outline: 'none', marginBottom: '8px' }}
        />
        <p style={{ fontSize: '11px', color: error ? V.rose : V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginBottom: '20px', lineHeight: 1.6 }}>
          {error ? 'Please enter your first name to continue.' : 'This helps us personalize your experience and your mentor know who is sending feedback.'}
        </p>
        <button onClick={handleSubmit}
          style={{ ...btnGold, width: '100%', padding: '14px', fontSize: '14px' }}>
          Let's begin →
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SECTION 2 — LYNN'S INTRODUCTION LETTER
// ═══════════════════════════════════════════════════════════════
const TRADING_RULES_DATA = [
  {
    category: 'Market Wisdom',
    rules: [
      { id: 'mw1', text: 'Buy the rumor, sell the news.', source: 'Wall Street adage', explanation: "Markets move in anticipation of events — not the events themselves. By the time the news is confirmed, the move is often already priced in. Smart traders position early and exit when everyone else is just arriving." },
      { id: 'mw2', text: "Don't fight the Fed.", source: 'Wall Street adage', explanation: "When the Federal Reserve is raising rates the market tends to struggle. When cutting it tends to rise. Trading against the Fed's direction is swimming upstream. Know which way the current is moving before you trade." },
      { id: 'mw3', text: 'Sell in May and go away.', source: 'Wall Street seasonal adage', explanation: "Historically the market underperforms from May through October compared to November through April. Not a rule to follow blindly — but worth knowing when positioning for longer trades." },
      { id: 'mw4', text: 'The Santa Claus Rally.', source: 'Yale Hirsch, Stock Trader\'s Almanac', explanation: "Markets historically show strength in late December through early January. Often attributed to holiday optimism, tax considerations, and institutional window dressing." },
      { id: 'mw5', text: 'The January Effect.', source: 'Investment research', explanation: "Small cap stocks historically outperform in January as investors redeploy capital after year-end tax-loss selling. Less reliable in recent years but worth knowing." },
      { id: 'mw6', text: 'Triple witching volatility.', source: 'Options market mechanics', explanation: "The third Friday of March, June, September, and December — when stock options, stock index futures, and stock index options all expire simultaneously. Expect elevated volatility and wider spreads. Consider sizing down." },
      { id: 'mw7', text: 'Election year patterns.', source: 'Historical market data', explanation: "Markets historically rise in election years as incumbents favor economic stability. Not a guarantee — but a useful backdrop when assessing longer term trades." },
      { id: 'mw8', text: "Don't fight the tape.", source: 'Jesse Livermore', explanation: "The tape — price action — is telling you something right now. Your opinion about what it should be doing is irrelevant. Read what is actually there and trade what you see." },
    ]
  },
  {
    category: 'Options Seller Rules',
    rules: [
      { id: 'os1', text: 'Sell premium when IV is high, avoid when IV is low.', source: 'tastylive research', explanation: "Implied volatility is the price of options. When IV is elevated you collect more premium for the same risk. When IV is low premium is cheap — the risk/reward favors buyers, not sellers. Check the VIX and IV percentile before every trade." },
      { id: 'os2', text: 'The 30-45 DTE sweet spot.', source: 'tastylive research', explanation: "Theta decay accelerates between 30 and 45 days to expiration. Opening trades in this window maximizes the rate at which time works in your favor. Closing at 50% profit captures most of that decay efficiently." },
      { id: 'os3', text: "Close at 50% profit — don't get greedy.", source: 'tastylive research', explanation: "Taking 50% of maximum profit removes you from the trade when most of the easy theta has been collected. Staying in for the last 50% introduces disproportionate risk for diminishing reward. Winners who become losers are avoidable." },
      { id: 'os4', text: "Never let a winner turn into a loser.", source: 'Trading wisdom', explanation: "Once a trade is profitable protect that profit. The market can reverse quickly. A small profit is always better than a loss. Set your exit rules before you enter — not while watching a position move against you." },
      { id: 'os5', text: 'The 2x loss rule.', source: 'Options trading risk management', explanation: "If the premium you collected doubles against you — close the trade. You sold a put for $1.50 and it is now worth $3.00 — close it. This rule prevents small losses from becoming catastrophic ones. No exceptions. No holding and hoping." },
      { id: 'os6', text: 'Size down in high VIX environments.', source: 'Risk management principles', explanation: "When VIX is elevated markets are moving faster and options pricing can be erratic. Smaller position sizes give you more room to be wrong and more time to respond. Rich premium is only valuable if you survive long enough to collect it." },
      { id: 'os7', text: 'Never sell premium into earnings.', source: 'Options trading risk management', explanation: "Earnings announcements cause unpredictable price gaps that can wipe out weeks of premium in minutes. Unless you specifically understand earnings strategies — avoid holding short premium through an earnings report." },
      { id: 'os8', text: 'Start at 0.15 delta for all selling strategies.', source: 'Risk management principles for beginning options sellers', explanation: "Whether selling a CSP, covered call, or spread — beginning options sellers should target the 0.15 delta range on the short strike. This gives approximately 85% probability of expiring worthless across all strategies.\n\nHigher win rate. More cushion. Less emotional pressure while you develop your trading discipline.\n\nThe delta range to progress through:\n0.15 — Beginner (start here)\n0.20-0.25 — Intermediate\n0.30 — Experienced\n\nMaster each level before advancing. There is no rush. Consistent small wins compound into significant wealth." },
      { id: 'os9', text: 'Undefined risk is not unlimited risk — but treat it that way.', source: 'Options education', explanation: "A naked put technically has defined risk — the stock going to zero. But treating it as if the risk is unlimited forces you to size conservatively and manage actively. This mindset protects accounts." },
      { id: 'os10', text: 'Check IV Rank before entering any new position. Above 30 preferred.', source: 'Trade Like a Woman — Tier 1 Program', explanation: "IV Rank shows where current implied volatility sits relative to the past year. IV Rank above 30 means options are more expensive than usual — you are being paid well for the risk you are taking.\n\nIV Rank below 30: premium is thin. The risk/reward may not justify entering.\nIV Rank below 15: reduce size significantly or wait for better conditions. The premium does not justify the risk.\n\nThis is not about predicting volatility. It is about ensuring you are paid fairly before you take the trade." },
      { id: 'os11', text: 'No new positions on Triple Witching day.', source: 'Options market mechanics', explanation: "The third Friday of March, June, September, and December. Three contract types expire simultaneously — stock index futures, stock index options, and individual stock options. Volume surges. Volatility is unpredictable. Fills can be unusual.\n\nStand aside or manage existing positions only. Mark these four dates on your calendar at the start of every year." },
    ]
  },
  {
    category: 'Trend Rules',
    rules: [
      { id: 'tr1', text: 'Trade the trend, not your opinion.', source: 'Ed Seykota', explanation: "Your opinion about where the market should go is irrelevant. The chart is telling you where it is going. Read it. Respect it. Trade with it — not against it." },
      { id: 'tr2', text: 'The trend is your friend — until it ends.', source: 'Ed Seykota', explanation: "Trending markets reward patience and punish opinion. Ride trends with discipline. Exit when the evidence changes — not when you think it should change." },
      { id: 'tr3', text: "Don't catch a falling knife.", source: 'Wall Street adage', explanation: "When a stock is dropping fast — breaking support levels, selling accelerating — the instinct to buy the dip can be expensive. A falling knife looks cheap at every price on the way down. Wait for confirmation that the trend has changed before entering. Let the knife hit the floor first." },
      { id: 'tr4', text: 'Price above all three MAs — bullish bias.', source: 'Technical analysis', explanation: "When price is above the 5, 20, and 50 day moving averages and all three are fanning upward in order — that is a strong uptrend. This is the environment where covered calls and the wheel work best." },
      { id: 'tr5', text: 'Price below all three MAs — stay out or go smaller.', source: 'Technical analysis', explanation: "When price is below all three moving averages and they are fanning downward — that is a strong downtrend. Premium sellers should reduce size significantly or wait for a better environment." },
      { id: 'tr6', text: 'MAs fanning in order — strong trend.', source: 'Technical analysis', explanation: "When moving averages are properly stacked — fastest above middle above slowest in an uptrend, or reversed in a downtrend — and spreading apart, the trend has momentum. When they tangle and cross each other the market is choppy. Choppy markets eat premium sellers alive." },
      { id: 'tr7', text: 'VWAP tells you the bias for the day.', source: 'Institutional trading', explanation: "Price above VWAP for the session signals institutional buying pressure — bullish bias. Price below VWAP signals selling pressure — bearish bias. For 0DTE traders VWAP is one of the most important intraday references." },
    ]
  },
  {
    category: 'Risk Management Rules',
    rules: [
      { id: 'rm1', text: 'Never risk more than 1-2% on a single trade.', source: 'Professional risk management', explanation: "Position sizing is the skill that keeps you in the game long enough for your edge to work. A 1-2% max loss per trade means you could have 50 losing trades in a row and still have half your account. That is survivability." },
      { id: 'rm2', text: 'Never have more than 5% of portfolio at risk at one time.', source: 'Professional risk management', explanation: "Total open risk across all positions should not exceed 5% of your account. This ensures a bad day in the market cannot be a catastrophic day for your account." },
      { id: 'rm3', text: 'Define your max loss before you enter — not after.', source: 'Mark Douglas, Trading in the Zone', explanation: "Every trade needs three things defined before entry: where you are wrong, where you are taking profit, and how much you are risking. Without these you are not trading — you are gambling." },
      { id: 'rm4', text: 'Cash is a position.', source: 'Warren Buffett', explanation: "Not being in a trade is a valid and often wise choice. Waiting for your exact setup, your exact conditions, your exact risk profile — and then acting decisively — is how professional traders operate. The market will always offer another opportunity." },
      { id: 'rm5', text: "If you wouldn't enter this trade fresh today — close it.", source: 'Trading wisdom', explanation: "The reason you stay in a trade should be as strong today as the day you entered it. If the thesis is no longer valid, if the setup has changed, if you are only holding because you want it to work — that is not a reason to hold. Close it." },
      { id: 'rm6', text: 'Cut losses. Let winners run to target.', source: 'Trading wisdom', explanation: "Most traders do the opposite — they hold losers hoping they recover and sell winners early out of fear. The math of trading rewards doing the uncomfortable thing: exiting quickly when wrong and staying patient when right." },
      { id: 'rm7', text: 'Never add to a losing position.', source: 'Jesse Livermore', explanation: "Adding to a losing position — averaging down — turns a small mistake into a large one. The market is telling you something. Listen to it. Exit and reassess from the outside — not from inside a deepening loss." },
      { id: 'rm8', text: 'The first loss is the best loss.', source: 'Trading wisdom', explanation: "Cutting a losing trade early is almost always cheaper than holding and hoping. The instinct to hold and hope is one of the most expensive habits a trader can develop. Take the small loss. Move on. The next setup is coming." },
      { id: 'rm9', text: 'Know your exit before you enter.', source: 'Trading wisdom', explanation: "Before you place any trade you should know exactly: where you are wrong, where you are taking profit, and how much you are risking. If you cannot answer all three — you are not ready to enter." },
      { id: 'rm10', text: 'Hope is not a trading strategy.', source: 'Trading wisdom', explanation: "Staying in a trade because you hope it will come back is not a plan. It is a wish. Plans have rules. Wishes do not. Know the difference before you enter — not while watching a position move against you." },
    ]
  },
  {
    category: 'Market Sayings',
    rules: [
      { id: 'ms1', text: 'Be fearful when others are greedy, and greedy when others are fearful.', source: 'Warren Buffett', explanation: "When the Fear & Greed index shows extreme fear and everyone is running — that is often when premium is richest and setups are best for sellers. When everyone is comfortable and greedy — that is when surprises happen. The crowd is almost always wrong at the extremes." },
      { id: 'ms2', text: 'Bulls make money, bears make money, pigs get slaughtered.', source: 'Wall Street adage', explanation: "Greed is the enemy of consistency. Taking your profit target and moving on is a skill. Staying in a trade because you want more — past your defined exit — is how winners become losers. Take the money." },
      { id: 'ms3', text: 'The market can stay irrational longer than you can stay solvent.', source: 'John Maynard Keynes', explanation: "Being right about direction does not matter if your timing is wrong and your position gets wiped out before the move happens. Always size for survival — not for being right." },
      { id: 'ms4', text: 'No one ever went broke taking a profit.', source: 'Wall Street adage', explanation: "A gentle reminder that partial exits, locking in gains, and not needing to catch the absolute top or bottom is how professional traders build wealth quietly and consistently over time." },
      { id: 'ms5', text: 'The market rewards patience and punishes impatience.', source: 'Trading wisdom', explanation: "Waiting for your exact setup — your exact entry, your exact conditions — and then acting decisively is the entire game. Impatient traders chase. Patient traders wait. Over time the difference in results is enormous." },
      { id: 'ms6', text: 'Volume precedes price.', source: 'Technical analysis', explanation: "When volume spikes before a big price move it is often smart money positioning. Unusual volume before a breakout or breakdown is worth paying attention to — even before the chart pattern confirms the move." },
      { id: 'ms7', text: "It's not about being right — it's about making money when you're right.", source: 'Mark Douglas', explanation: "A trader can be right 40% of the time and be very profitable if their wins are larger than their losses. Win rate is only half the equation. Position sizing and letting winners run to target is the other half." },
    ]
  },
];
function CommunityView({ onBack }) {
  const benefits = [
    { icon: '💬', title: 'Ask questions in a safe space', body: 'No judgment, no jargon gatekeeping. Every question is a good question.' },
    { icon: '🎉', title: 'Celebrate wins with people who get it', body: 'Your first premium collected, first assigned trade, first month profitable — we cheer together.' },
    { icon: '📖', title: 'Learn from each other\'s trades', body: 'Real trades, real outcomes, real lessons. The community is the curriculum.' },
  ];
  return (
    <div style={{ padding: '40px 24px', maxWidth: '680px', margin: '0 auto' }}>
      <button style={{ ...btnGhost, marginBottom: '28px', fontSize: '12px' }} onClick={onBack}>← Dashboard</button>

      <div style={{ textAlign: 'center', marginBottom: '36px' }}>
        <div style={{ fontSize: '32px', color: V.gold, marginBottom: '12px' }}>♀</div>
        <h1 style={{ ...serif, fontSize: '34px', fontWeight: 500, color: V.text, marginBottom: '10px' }}>
          The Trade Like a Woman Community
        </h1>
        <p style={{ ...serif, fontSize: '22px', fontStyle: 'italic', color: V.gold, marginBottom: '20px' }}>
          You don't have to do this alone.
        </p>
        <p style={{ fontSize: '15px', lineHeight: 1.85, color: 'rgba(240,237,230,0.8)', maxWidth: '520px', margin: '0 auto 32px' }}>
          Trading can feel isolating — especially in the beginning. That's why I created a private space just for us. Ask questions, share wins, celebrate milestones, and learn alongside other women who are on the same path. I'm in there too.
        </p>
        <a
          href="https://www.facebook.com/groups/1519037119571290/members"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-block', padding: '16px 36px', background: 'rgba(201,168,76,0.12)', border: '0.5px solid rgba(201,168,76,0.5)', borderRadius: '10px', color: V.goldL, fontSize: '15px', fontWeight: 500, letterSpacing: '0.06em', textDecoration: 'none', transition: 'background 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.22)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(201,168,76,0.12)'}
        >
          Join Our Facebook Group →
        </a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '28px' }}>
        {benefits.map((b, i) => (
          <div key={i} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '20px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: '26px', marginBottom: '10px' }}>{b.icon}</div>
            <div style={{ ...serif, fontSize: '16px', fontWeight: 500, color: V.text, marginBottom: '8px', lineHeight: 1.3 }}>{b.title}</div>
            <div style={{ fontSize: '12px', color: V.muted, lineHeight: 1.65 }}>{b.body}</div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center', fontSize: '12px', color: V.dim }}>
        🔒 This is a private group — your information stays within our community.
      </div>
    </div>
  );
}

function PortfolioManagement({ onNavigate }) {
  const priorities = [
    { n: '①', color: '#c47a7a', border: 'rgba(196,122,122,0.4)', title: '2X LOSS RULE TRIGGERED', body: 'Close immediately. No exceptions. Do not let a position at 2x wait while you manage others.' },
    { n: '②', color: '#c9a84c', border: 'rgba(201,168,76,0.3)', title: 'APPROACHING EXPIRATION (7 DAYS OR LESS)', body: 'Gamma risk is increasing sharply. Evaluate close or roll today. Do not let short options expire in the money.' },
    { n: '③', color: '#c9a84c', border: 'rgba(201,168,76,0.3)', title: 'POSITION AT YOUR STRIKE', body: 'Stock at or near your put strike. Evaluate roll vs close vs assign. This needs attention today.' },
    { n: '④', color: '#7aab8a', border: 'rgba(122,171,138,0.35)', title: 'AT 50% PROFIT', body: 'Close it. Do not let profitable positions give back gains unnecessarily.' },
    { n: '⑤', color: 'rgba(240,237,230,0.4)', border: 'rgba(240,237,230,0.08)', title: 'EVERYTHING ELSE', body: 'New entries and research are last. Managing open positions always comes before entering new ones.' },
  ];

  const dailyCards = [
    { time: 'BEFORE MARKET OPEN — 5 MIN', steps: 'Check Risk Dashboard.\nNote available capital.\nReview positions with DTE under 14 days.\nCheck for earnings on any held stocks.' },
    { time: 'MARKET OPEN — 5 MIN', steps: 'Scan for overnight moves.\nAddress 2x loss triggers first.\nCheck anything that moved more than 3% overnight.' },
    { time: 'MID SESSION — 5 MIN (optional)', steps: 'Check for positions at 50% profit.\nClose them and reset GTC orders.' },
    { time: 'BEFORE CLOSE — 5 MIN', steps: 'Review positions approaching management thresholds.\nConfirm all GTC orders active.' },
  ];

  const sectionTitle = (t) => (
    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: '#c9a84c', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>{t}</div>
  );

  return (
    <div style={{ maxWidth: '780px', margin: '0 auto', padding: '32px 24px', fontFamily: "'Jost', sans-serif" }}>
      <button onClick={() => onNavigate('dashboard')} style={{ background: 'none', border: 'none', color: 'rgba(240,237,230,0.4)', fontSize: '13px', cursor: 'pointer', marginBottom: '24px', display: 'block', fontFamily: "'Jost',sans-serif" }}>
        ← Dashboard
      </button>

      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '32px', color: '#c9a84c', marginBottom: '8px' }}>Portfolio Management</div>
      <div style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', fontStyle: 'italic', marginBottom: '32px', lineHeight: '1.8' }}>
        Managing multiple positions requires a different kind of thinking than managing one. This is where The Foundation becomes a portfolio.
      </div>

      {/* When to Add */}
      {sectionTitle('When to Add a New Position')}
      <div style={{ background: '#111114', border: '0.5px solid rgba(240,237,230,0.08)', borderRadius: '10px', padding: '20px', marginBottom: '24px' }}>
        <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', lineHeight: 1.9, margin: '0 0 14px', whiteSpace: 'pre-line' }}>Add a new position only when:</p>
        {[
          'Current positions are stable and not requiring attention',
          'You have a clear setup that meets all entry criteria',
          'The new position would not push total risk above your daily max loss limit if everything went wrong at once',
          'You have enough buying power to manage existing positions if they move against you',
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: i < 3 ? '10px' : 0 }}>
            <span style={{ color: '#7aab8a', fontWeight: 600, flexShrink: 0 }}>✓</span>
            <span style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', lineHeight: 1.75 }}>{item}</span>
          </div>
        ))}
        <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', lineHeight: 1.9, margin: '14px 0 0', whiteSpace: 'pre-line' }}>
          {'Start with one.\nAdd a second when the first is working.\nAdd a third when the second is working.\nBuild slowly.\nSurvive first. Grow second.'}
        </p>
      </div>

      {/* Priority Order */}
      {sectionTitle('When Multiple Positions Need Attention on the Same Day')}
      <div style={{ background: '#111114', border: '0.5px solid rgba(201,168,76,0.2)', borderRadius: '10px', padding: '20px', marginBottom: '24px' }}>
        {priorities.map((row, i) => (
          <div key={i} style={{ display: 'flex', gap: '14px', padding: '12px 0', borderBottom: i < 4 ? '0.5px solid rgba(240,237,230,0.06)' : 'none' }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', color: row.color, flexShrink: 0, lineHeight: 1, minWidth: '24px' }}>{row.n}</div>
            <div>
              <div style={{ fontSize: '11px', color: row.color, fontWeight: 700, letterSpacing: '0.05em', marginBottom: '4px' }}>{row.title}</div>
              <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', lineHeight: 1.7, margin: 0 }}>{row.body}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Correlation Risk */}
      {sectionTitle('Correlation — The Hidden Risk')}
      <div style={{ background: '#111114', border: '0.5px solid rgba(196,122,122,0.25)', borderRadius: '10px', padding: '20px', marginBottom: '24px' }}>
        {[
          'If you have CSPs open on five tech stocks and the Nasdaq sells off sharply — all five positions move against you simultaneously.',
          'This is correlation risk. Your positions felt diversified but they were all exposed to the same sector move.',
          'True diversification means positions across different sectors:\n\nTechnology + Consumer Staples + Healthcare + Energy + Financials\n\nA sector-wide move then only affects one or two positions instead of all of them.',
          'As you build your watchlist deliberately include stocks from different sectors.',
        ].map((p, i) => (
          <p key={i} style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', lineHeight: 1.9, margin: i > 0 ? '12px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
        ))}
      </div>

      {/* Reading Positions in TOS */}
      {sectionTitle('Reading Your Positions in TOS')}
      <div style={{ background: '#111114', border: '0.5px solid rgba(240,237,230,0.08)', borderRadius: '10px', padding: '20px', marginBottom: '24px' }}>
        <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', lineHeight: 1.8, margin: '0 0 14px' }}>Go to Monitor tab → Positions and Simulated Trades.</p>
        {[
          { label: 'DELTA', text: 'How much is this position moving with the stock right now? As delta increases on a short put your risk is increasing.' },
          { label: 'DAYS TO EXPIRATION (DTE)', text: 'Below 21 days — start planning. Below 7 days — act today.' },
          { label: 'P/L OPEN', text: 'Your unrealized gain or loss since the position was opened. At 50% profit — close it. At 2x loss — close it. Between those — hold and monitor.' },
        ].map((row, i) => (
          <div key={i} style={{ marginBottom: i < 2 ? '10px' : 0 }}>
            <span style={{ fontSize: '11px', color: '#d4b96a', fontWeight: 700, letterSpacing: '0.05em' }}>{row.label}: </span>
            <span style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', lineHeight: 1.8 }}>{row.text}</span>
          </div>
        ))}
      </div>

      {/* Daily Routine */}
      {sectionTitle('Daily Routine — Managing Multiple Positions')}
      <div style={{ background: '#111114', border: '0.5px solid rgba(201,168,76,0.2)', borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
        {dailyCards.map((row, i) => (
          <div key={i} style={{ marginBottom: i < 3 ? '14px' : 0, paddingBottom: i < 3 ? '14px' : 0, borderBottom: i < 3 ? '0.5px solid rgba(240,237,230,0.06)' : 'none' }}>
            <div style={{ fontSize: '11px', color: '#d4b96a', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '6px' }}>{row.time}</div>
            <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{row.steps}</p>
          </div>
        ))}
        <p style={{ fontSize: '12px', color: 'rgba(240,237,230,0.35)', fontStyle: 'italic', lineHeight: 1.8, marginTop: '14px', margin: '14px 0 0' }}>
          Total daily time: 20 minutes. Options selling is not day trading. You are not watching screens all day. You are checking in, managing when needed, and letting theta do the work.
        </p>
      </div>
    </div>
  );
}

function TaxHubView({ onBack }) {
  return (
    <div style={{ padding: '40px 32px', maxWidth: '680px' }}>
      <button style={{ ...btnGhost, marginBottom: '28px', fontSize: '12px' }} onClick={onBack}>← Dashboard</button>
      <div style={{ display: 'inline-block', padding: '4px 12px', background: 'rgba(201,168,76,0.08)', border: `0.5px solid ${V.border}`, borderRadius: '20px', fontSize: '10px', color: V.gold, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", marginBottom: '14px' }}>Coming Soon</div>
      <h1 style={{ ...serif, fontSize: '34px', fontWeight: 400, color: V.gold, margin: '0 0 12px' }}>Tax Hub</h1>
      <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost', sans-serif", lineHeight: 1.8, marginBottom: '28px', maxWidth: '520px' }}>
        Options trading has unique tax implications — wash sale rules, 60/40 treatment for index options, and tracking cost basis across assignments. This section will help you stay organized so tax season doesn't become a surprise.
      </p>
      <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '24px' }}>
        <p style={{ ...serif, fontSize: '17px', color: V.dim, fontStyle: 'italic', lineHeight: 1.7, margin: 0 }}>
          "The best traders think about tax efficiency from trade one, not at year end."
        </p>
      </div>
    </div>
  );
}

// ─── CONFIG / ACCOUNT SETTINGS VIEW ─────────────────────────────
// ═══════════════════════════════════════════════════════════════
// ADMIN FEEDBACK DASHBOARD
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// FIRST PAPER TRADE CAPSTONE
// ═══════════════════════════════════════════════════════════════
// SECTION 8 — SIDEBAR NAVIGATION
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// SECTION 9 — SCENARIO LIBRARY (abbreviated inline version)
// Full version in ScenarioLibrary.jsx — import that file for
// the complete 8-scenario library with all explanations.
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// CSP & BULL PUT SPREAD SIMULATOR
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// PRACTICE THE GREEKS
// ═══════════════════════════════════════════════════════════════
// placeholder — kept so nothing below breaks
// ═══════════════════════════════════════════════════════════════
// TRADING MANUAL
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// GLOSSARY
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════
export default function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showLanding, setShowLanding] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setAuthLoading(false);
      if (s?.user) {
        loadDataFromSupabase(s.user.id)
          .then(() => migrateLocalDataToSupabase(s.user.id));
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, s) => {
      setSession(s);
      if (s?.user) {
        await loadDataFromSupabase(s.user.id);
        await migrateLocalDataToSupabase(s.user.id);
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  const [state, setState] = useState(() => {
    try { const saved = localStorage.getItem('tlw_foundation_state'); return saved ? { ...INITIAL_STATE, ...JSON.parse(saved) } : INITIAL_STATE; } catch { return INITIAL_STATE; }
  });
  const [view, setView] = useState(() => {
    try {
      // Check dedicated key first, fall back to legacy state for existing users
      const legacyState = JSON.parse(localStorage.getItem('tlw_foundation_state') || '{}');
      const disclaimerDone = localStorage.getItem('tlaw_disclaimer') === 'true' || legacyState.disclaimerSigned === true;
      if (!disclaimerDone) return 'disclaimer';
      const introDone = localStorage.getItem('tlaw_intro') === 'true' || legacyState.introLetterRead === true;
      if (!introDone) return 'intro';
      return localStorage.getItem('tlaw_lastView') || 'dashboard';
    } catch { return 'disclaimer'; }
  });
  const [pendingReflection, setPendingReflection] = useState(null);

  // Persist full state
  useEffect(() => {
    try { localStorage.setItem('tlw_foundation_state', JSON.stringify(state)); } catch {}
  }, [state]);

  // Persist current view so returning users land where they left off
  useEffect(() => {
    if (view !== 'disclaimer') {
      try { localStorage.setItem('tlaw_lastView', view); } catch {}
    }
  }, [view]);

  const [isMobileApp, setIsMobileApp] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    const handler = () => {
      const m = window.innerWidth < 768;
      setIsMobileApp(m);
      if (!m) setSidebarOpen(false);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const update = useCallback((patch) => setState(s => ({ ...s, ...patch })), []);

  const navigate = useCallback((v) => setView(v), []);

  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
  }, []);

  // Sync student name from auth session metadata (runs once after session is available)
  useEffect(() => {
    if (!session) return;
    const metaName = session.user?.user_metadata?.first_name;
    if (metaName && !localStorage.getItem('tlaw_student_name')) {
      try { localStorage.setItem('tlaw_student_name', metaName); } catch {}
      update({ studentName: metaName });
    }
  }, [session]); // eslint-disable-line

  // Redirect any unrecognized view to dashboard to prevent blank content area
  useEffect(() => {
    const known = new Set([
      'dashboard','tradebuilder','simulator','practice_greeks','candles','ind_candles_practice',
      'ma','practice_ma','stockselection','scenarios','pnl','journal',
      'tradingrules','manual','tradesetup','test','admin_feedback','community',
      'innerwork','innerwork_pretrade','innerwork_reflect','innerwork_mirror',
      'innerwork_resources','glossary','icharts','strategycharts','full_trade_simulator',
      'ch_riskmanagement','adv_reading','tools','marketbrief','calendar',
      'lesson_candlesticks','lesson_ma','lesson_rsi','lesson_greeks','lesson_bb',
      'ind_candles_lesson','ind_ma_lesson','ind_rsi_lesson','ind_greeks_lesson',
      'lesson_vwap','practice_vwap','practice_rsi','ind_bb_practice','ind_greeks_practice',
      'support_resistance_practice','confluence_practice','spread_builder_practice','roll_calculator',
      'chapter_ch00','taxhub','0dte_sim','meditations','config','portfolio_management',
      'calculators','calc_position','calc_options','calc_breakeven','calc_rr','calc_compound',
      'risk_dashboard','first_paper_trade','admin','spx_live','spx_confluence',
      ...Object.keys(LOCKED_CALCS),
    ]);
    if (view !== 'disclaimer' && view !== 'intro' && !view.startsWith('chapter_') && !known.has(view)) {
      console.warn('[TLAW] Unknown view "' + view + '" — redirecting to dashboard');
      setView('dashboard');
    }
  }, [view]);

  const speech = useSpeech();

  // Disclaimer complete
  const onDisclaimerComplete = (name) => {
    const firstName = name.split(' ')[0];
    try {
      localStorage.setItem('tlaw_disclaimer', 'true');
      localStorage.setItem('tlaw_student_name', firstName);
    } catch {}
    update({ disclaimerSigned: true, studentName: firstName, xpTotal: state.xpTotal + 50 });
    setView('intro');
  };

  // Intro letter complete
  const onIntroComplete = () => {
    try { localStorage.setItem('tlaw_intro', 'true'); } catch {}
    update({ introLetterRead: true, xpTotal: state.xpTotal + 30 });
    setView('dashboard');
  };

  // Chapter quiz pass
  const onQuizPass = (chapterId, score) => {
    const newCompleted = state.completedChapters.includes(chapterId) ? state.completedChapters : [...state.completedChapters, chapterId];
    const nextIdx = CHAPTERS.findIndex(c => c.id === chapterId) + 1;
    const nextChapter = nextIdx < CHAPTERS.length ? CHAPTERS[nextIdx].id : chapterId;
    update({ completedChapters: newCompleted, chapterScores: { ...state.chapterScores, [chapterId]: score }, activeChapter: nextChapter, xpTotal: state.xpTotal + Math.round(score * 1.5) });
  };

  // Trade builder complete
  const onTradeComplete = (score) => {
    update({ tradeBuilderCount: state.tradeBuilderCount + 1, tradeBuilderScores: [...state.tradeBuilderScores, score], paperTradeDays: state.paperTradeDays + 1, xpTotal: state.xpTotal + (score >= 80 ? 150 : score >= 60 ? 80 : 40) });
  };

  // Auth loading spinner
  if (authLoading) return (
    <div style={{ minHeight: '100vh', background: '#0e0e10', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '20px', color: 'rgba(201,168,76,0.5)' }}>♀</div>
    </div>
  );

  // Auth screen — shown if no session
  if (!session) {
    if (showLanding) return <LandingPage onEnterLogin={() => setShowLanding(false)} />;
    return <><FontLoader /><AuthScreen onAuthSuccess={(s) => setSession(s)} /></>;
  }

  // Gated views
  if (view === 'disclaimer') return <><FontLoader /><DisclaimerGate onComplete={onDisclaimerComplete} /></>;
  if (view === 'intro')      return <><FontLoader /><LynnIntroLetter studentName={state.studentName} onComplete={onIntroComplete} speech={speech} /></>;

  // Main app layout with sidebar
  const isChapterView = view.startsWith('chapter_') && view !== 'chapter_ch00';
  const chapterId = isChapterView ? view.replace('chapter_', '') : null;

  return (
    <AppErrorBoundary>
      <FontLoader />
      <div style={{ display: 'flex', flexDirection: 'row', width: '100%', minHeight: '100vh', background: V.bg, maxWidth: '100vw', overflowX: 'hidden' }}>
        {/* Mobile overlay — closes sidebar when tapping outside */}
        {isMobileApp && sidebarOpen && (
          <div onClick={() => setSidebarOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 999 }} />
        )}
        <Sidebar
          currentView={view}
          onNavigate={(v) => { navigate(v); if (isMobileApp) setSidebarOpen(false); }}
          state={state}
          speech={speech}
          isMobile={isMobileApp}
          isOpen={sidebarOpen}
          session={session}
          onSignOut={handleSignOut}
        />
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%', minWidth: 0, overflowY: 'auto', overflowX: 'hidden', position: 'relative', zIndex: 0, background: V.bg }}>
          {/* Mobile top bar with hamburger */}
          {isMobileApp && (
            <div style={{ position: 'sticky', top: 0, zIndex: 100, background: V.bg, borderBottom: `0.5px solid ${V.border}`, padding: '0 16px', display: 'flex', alignItems: 'center', height: '52px', gap: '14px' }}>
              <button onClick={() => setSidebarOpen(o => !o)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px 6px', color: V.gold, fontSize: '20px', lineHeight: 1, display: 'flex', alignItems: 'center', minWidth: '44px', minHeight: '44px', justifyContent: 'center' }}>
                ☰
              </button>
              <div style={{ ...serif, fontSize: '15px', color: V.goldL, fontWeight: 500 }}>Trade Like a Woman</div>
            </div>
          )}
          {view === 'dashboard'     && <ProgressDashboard state={state} onNavigate={navigate} onUpdate={update} tradingRulesData={TRADING_RULES_DATA} />}
          {isChapterView            && <ChapterViewer chapterId={chapterId} state={state} onQuizPass={onQuizPass} onBack={() => navigate('dashboard')} speech={speech} onNavigate={navigate} onPractice={(returnView, returnTab) => setState(s => ({ ...s, practiceReturnView: returnView, practiceReturnTab: returnTab }))} visuals={CHAPTER_VISUALS} />}
          {view === 'tradebuilder'  && <TradeBuilderWizard onTradeComplete={onTradeComplete} onBack={() => navigate('dashboard')} state={state} onUpdate={update} />}
          {view === 'simulator'     && <OptionsChainSimulator state={state} onUpdate={update} onBack={() => { const rv = state.practiceReturnView; const rt = state.practiceReturnTab; if (rv) { const chId = rv.replace('chapter_', ''); if (chId === 'ch04' && rt != null) { try { localStorage.setItem('tlaw_ch04_return_section', String(rt)); } catch {} } if (chId === 'ch05' && rt != null) { try { localStorage.setItem('tlaw_ch05_return_section', String(rt)); } catch {} } if (chId === 'ch06' && rt != null) { try { localStorage.setItem('tlaw_ch06_return_section', String(rt)); } catch {} } setState(s => ({ ...s, practiceReturnView: null, practiceReturnTab: null })); navigate(rv); } else { try { const v = localStorage.getItem('tlaw_simulator_return'); if (v) { localStorage.removeItem('tlaw_simulator_return'); navigate(v); return; } } catch {} navigate('dashboard'); } }} onNavigate={navigate} />}
          {view === 'practice_greeks'          && <PracticeTheGreeks onNavigate={navigate} />}
          {view === 'spread_builder_practice' && <CSPSimulator onNavigate={navigate} />}
          {view === 'roll_calculator'         && <RollCalculatorView onNavigate={navigate} />}
          {view === 'candles'             && <CandlestickSimulator onXpAward={(xp) => update({ xpTotal: state.xpTotal + xp })} onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'ind_candles_practice' && <CandleSimWrapper onXpAward={(xp) => update({ xpTotal: state.xpTotal + xp })} onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'ma'                  && <MovingAverageSimulator xpAward={(xp) => update({ xpTotal: state.xpTotal + xp })} onBack={() => navigate('dashboard')} />}
          {view === 'practice_ma'         && <MASimWrapper xpAward={(xp) => update({ xpTotal: state.xpTotal + xp })} onBack={() => navigate('dashboard')} />}
          {view === 'stockselection'&& <StockSelectionChapter state={state} onComplete={update} onBack={() => navigate('dashboard')} />}
          {view === 'scenarios'     && <ScenarioLibrary state={state} onUpdate={update} onBack={() => navigate('dashboard')} />}
          {view === 'pnl'           && <PLTracker state={state} onUpdate={update} onBack={() => navigate('dashboard')} onNavigate={navigate} onReflect={(trade) => { setPendingReflection(trade); navigate('innerwork'); }} />}
          {view === 'journal'       && <TradingJournal state={state} onUpdate={update} onBack={() => navigate('dashboard')} />}
          {view === 'tradingrules'  && <TradingRulesView state={state} onUpdate={update} onBack={() => navigate('dashboard')} tradingRulesData={TRADING_RULES_DATA} />}
          {view === 'manual'        && <ManualView onBack={() => navigate('dashboard')} />}
          {view === 'tradesetup'    && <TradeSetupView onBack={() => navigate('dashboard')} />}
          {view === 'test'          && <ReadinessTestView state={state} onUpdate={update} onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'admin'          && session?.user?.email === 'lynnds5@gmail.com' && <AdminDashboard session={session} onNavigate={navigate} />}
          {view === 'spx_live'       && (session?.user?.email === 'lynnds5@gmail.com' ? <SPXConfluenceLive /> : (
            <div style={{ padding: '40px 24px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ background: V.bg2, border: `1px solid ${V.border}`, borderRadius: '14px', padding: '48px 40px', maxWidth: '480px', width: '100%', textAlign: 'center' }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', color: V.gold, margin: '0 0 12px' }}>This tool is not available on your current plan.</div>
                <button onClick={() => navigate('dashboard')} style={{ padding: '10px 22px', background: 'transparent', border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", marginTop: '8px' }}>← Back</button>
              </div>
            </div>
          ))}
          {view === 'spx_confluence' && (state.tier2Unlocked ? <SPXConfluence /> : (
            <div style={{ padding: '40px 24px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ background: V.bg2, border: `1px solid ${V.border}`, borderRadius: '14px', padding: '48px 40px', maxWidth: '520px', width: '100%', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>🔒</div>
                <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '26px', fontWeight: 400, color: V.gold, margin: '0 0 12px' }}>SPX Confluence</h2>
                <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.75, marginBottom: '24px' }}>This tool unlocks with Tier 2 access from your mentor. Master CSPs, Covered Calls, and The Wheel first.</p>
                <button onClick={() => navigate('dashboard')} style={{ padding: '10px 22px', background: 'transparent', border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}>← Back</button>
              </div>
            </div>
          ))}
          {view === 'admin_feedback' && (state.adminMode || session?.user?.email === 'lynnds5@gmail.com') && <AdminFeedbackView onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'community'     && <CommunityView onBack={() => navigate('dashboard')} />}
          {view === 'innerwork'          && <InnerTrader state={state} onUpdate={update} onBack={() => { setPendingReflection(null); navigate('dashboard'); }} initialTrade={pendingReflection} />}
          {view === 'innerwork_pretrade' && <InnerTrader state={state} onUpdate={update} onBack={() => navigate('dashboard')} initialTab="pretrade" />}
          {view === 'innerwork_reflect'  && <InnerTrader state={state} onUpdate={update} onBack={() => navigate('dashboard')} initialTab="posttrade" />}
          {view === 'innerwork_mirror'   && <InnerTrader state={state} onUpdate={update} onBack={() => navigate('dashboard')} initialTab="mirror" />}
          {view === 'innerwork_resources'&& <InnerTrader state={state} onUpdate={update} onBack={() => navigate('dashboard')} initialTab="resources" />}
          {view === 'glossary'           && <GlossaryView onBack={() => navigate('dashboard')} />}
          {view === 'strategycharts' && (
            <div style={{ padding: '32px 24px', maxWidth: '560px', margin: '0 auto' }}>
              <button style={{ ...btnGhost, padding: '7px 16px', fontSize: '12px', marginBottom: '28px' }} onClick={() => navigate('dashboard')}>
                ← Dashboard
              </button>
              <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '32px', fontWeight: 400, color: V.gold, margin: '0 0 16px' }}>The Full Trade Simulator</h2>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, marginBottom: '28px' }}>
                Strategy Charts has been replaced by The Full Trade Simulator — a more powerful interactive tool that lets you build real market conditions and practice every strategy in context.
              </p>
              <button style={{ ...btnGold, width: '100%', padding: '13px 24px', fontSize: '14px', marginBottom: '10px' }} onClick={() => navigate('full_trade_simulator')}>
                Open The Full Trade Simulator →
              </button>
              <button style={{ ...btnGhost, padding: '10px 22px', fontSize: '13px' }} onClick={() => navigate('chapter_ch06')}>
                ← Back to Chapter 6
              </button>
            </div>
          )}
          {view === 'full_trade_simulator' && <FullTradeSimulator onNavigate={navigate} />}
          {(view === 'icharts') && (() => {
            const rv = state.practiceReturnView;
            const chNum = rv ? parseInt(rv.replace('chapter_ch', ''), 10) : null;
            const iscBackLabel = chNum ? `← Back to Chapter ${chNum}` : '← Back';
            return <InteractiveStrategyCharts
              onBack={() => { if (rv) { setState(s => ({ ...s, practiceReturnView: null, practiceReturnTab: null })); navigate(rv); } else navigate('dashboard'); }}
              backLabel={iscBackLabel}
              onNavigate={navigate}
              tier2Unlocked={state.tier2Unlocked}
              unlockCode={TIER2_SESSION_CODE}
              onTier2Unlock={() => update({ tier2Unlocked: true })}
            />;
          })()}
          {view === 'ch_riskmanagement' && <RiskManagementView onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'adv_reading'       && <ReadingTheMarketView state={state} onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'tools'              && <ToolsHub trades={state.plTrades || []} onBack={() => navigate('dashboard')} xpAward={(xp) => update({ xpTotal: state.xpTotal + xp })} />}
          {view === 'marketbrief'        && <MarketBriefView onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'calendar'           && <CalendarView onBack={() => navigate('dashboard')} />}
          {['lesson_candlesticks','lesson_ma','lesson_rsi','lesson_greeks','lesson_bb'].includes(view) && (
            <IndicatorLessonView indicatorKey={view.replace('lesson_','')} onBack={() => navigate('dashboard')} onNavigate={navigate} />
          )}
          {/* Legacy view-name aliases — redirect to current routes */}
          {view === 'ind_candles_lesson'  && <IndicatorLessonView indicatorKey='candlesticks' onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'ind_ma_lesson'       && <IndicatorLessonView indicatorKey='ma'           onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'ind_rsi_lesson'      && <IndicatorLessonView indicatorKey='rsi'          onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'ind_greeks_lesson'   && <IndicatorLessonView indicatorKey='greeks'       onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'lesson_vwap' && (state.tier2Unlocked
            ? <IndicatorLessonView indicatorKey='vwap' onBack={() => navigate('dashboard')} onNavigate={navigate} />
            : <div style={{ padding:'40px 24px', display:'flex', justifyContent:'center' }}>
                <div style={{ background:V.bg2, border:`1px solid ${V.border}`, borderRadius:'14px', padding:'48px 40px', maxWidth:'520px', width:'100%', textAlign:'center' }}>
                  <div style={{ fontSize:'32px', marginBottom:'16px' }}>🔒</div>
                  <h2 style={{ ...serif, fontSize:'26px', fontWeight:400, color:V.gold, margin:'0 0 12px' }}>VWAP — Lesson</h2>
                  <p style={{ fontSize:'14px', color:V.muted, fontFamily:"'Jost',sans-serif", lineHeight:1.75, marginBottom:'24px' }}>This lesson unlocks when your mentor approves your Tier 2 access. Master CSPs, Covered Calls, and The Wheel first — then VWAP becomes your next tool.</p>
                  <button onClick={() => navigate('dashboard')} style={{ padding:'10px 22px', background:'transparent', border:`0.5px solid ${V.border}`, borderRadius:'8px', color:V.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Jost',sans-serif" }}>← Back</button>
                </div>
              </div>
          )}
          {view === 'practice_vwap' && (state.tier2Unlocked
            ? <VWAPPracticeView onBack={() => navigate('dashboard')} onNavigate={navigate} />
            : <div style={{ padding:'40px 24px', display:'flex', justifyContent:'center' }}>
                <div style={{ background:V.bg2, border:`1px solid ${V.border}`, borderRadius:'14px', padding:'48px 40px', maxWidth:'520px', width:'100%', textAlign:'center' }}>
                  <div style={{ fontSize:'32px', marginBottom:'16px' }}>🔒</div>
                  <h2 style={{ ...serif, fontSize:'26px', fontWeight:400, color:V.gold, margin:'0 0 12px' }}>VWAP — Practice</h2>
                  <p style={{ fontSize:'14px', color:V.muted, fontFamily:"'Jost',sans-serif", lineHeight:1.75, marginBottom:'24px' }}>This practice session unlocks when your mentor approves your Tier 2 access. Master the foundation strategies first — then VWAP practice becomes your next step.</p>
                  <button onClick={() => navigate('dashboard')} style={{ padding:'10px 22px', background:'transparent', border:`0.5px solid ${V.border}`, borderRadius:'8px', color:V.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Jost',sans-serif" }}>← Back</button>
                </div>
              </div>
          )}
          {view === 'practice_rsi'       && <RSIPracticeView onBack={() => navigate('dashboard')} onNavigate={navigate} />}
          {view === 'support_resistance_practice' && <SupportResistancePracticeView state={state} onBack={() => navigate('dashboard')} onNavigate={navigate} onSmartBack={() => { const r = state.practiceReturnView; const t = state.practiceReturnTab; if (r) { try { if (r === 'chapter_ch04' && t != null) localStorage.setItem('tlaw_ch04_return_section', String(t)); else if (r === 'chapter_ch05' && t != null) localStorage.setItem('tlaw_ch05_return_section', String(t)); } catch {} setState(s => ({ ...s, practiceReturnView: null, practiceReturnTab: null })); navigate(r); } else navigate('dashboard'); }} />}
          {view === 'confluence_practice' && <ConfluencePracticeView state={state} onBack={() => navigate('dashboard')} onNavigate={navigate} onSmartBack={() => { const r = state.practiceReturnView; const t = state.practiceReturnTab; if (r) { try { if (r === 'chapter_ch04' && t != null) localStorage.setItem('tlaw_ch04_return_section', String(t)); else if (r === 'chapter_ch05' && t != null) localStorage.setItem('tlaw_ch05_return_section', String(t)); } catch {} setState(s => ({ ...s, practiceReturnView: null, practiceReturnTab: null })); navigate(r); } else navigate('dashboard'); }} />}
          {view === 'ind_bb_practice'     && <BollingerBandsPracticeView state={state} onBack={() => navigate('dashboard')} onNavigate={navigate} onSmartBack={() => { const r = state.practiceReturnView; const t = state.practiceReturnTab; if (r) { try { if (r === 'chapter_ch04' && t != null) localStorage.setItem('tlaw_ch04_return_section', String(t)); else if (r === 'chapter_ch05' && t != null) localStorage.setItem('tlaw_ch05_return_section', String(t)); } catch {} setState(s => ({ ...s, practiceReturnView: null, practiceReturnTab: null })); navigate(r); } else navigate('dashboard'); }} />}
          {view === 'ind_greeks_practice' && <GreeksPracticeView state={state} onBack={() => navigate('dashboard')} onNavigate={navigate} onOpenChain={() => { update({ arrivedFromGreeks: true }); navigate('simulator'); }} onSmartBack={() => { const r = state.practiceReturnView; const t = state.practiceReturnTab; if (r) { try { if (r === 'chapter_ch04' && t != null) localStorage.setItem('tlaw_ch04_return_section', String(t)); else if (r === 'chapter_ch05' && t != null) localStorage.setItem('tlaw_ch05_return_section', String(t)); } catch {} setState(s => ({ ...s, practiceReturnView: null, practiceReturnTab: null })); navigate(r); } else navigate('dashboard'); }} />}
          {view === 'chapter_ch00'  && <Ch0View state={state} onUpdate={update} onNavigate={navigate} onBack={() => navigate('dashboard')} />}
          {view === 'taxhub'         && <TaxHubView onBack={() => navigate('dashboard')} />}
          {view === 'portfolio_management' && (state.tier2Unlocked ? <PortfolioManagement onNavigate={navigate} /> : (
            <div style={{ padding: '40px 24px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ background: V.bg2, border: `1px solid ${V.border}`, borderRadius: '14px', padding: '48px 40px', maxWidth: '520px', width: '100%', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>🔒</div>
                <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '26px', fontWeight: 400, color: V.gold, margin: '0 0 12px' }}>Portfolio Management</h2>
                <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.75, marginBottom: '24px' }}>This module unlocks with Tier 2 access from your mentor. Master one position completely first — then Portfolio Management awaits you here.</p>
                <button onClick={() => navigate('dashboard')} style={{ padding: '10px 22px', background: 'transparent', border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}>← Back</button>
              </div>
            </div>
          ))}
          {view === 'risk_dashboard'    && <RiskDashboard state={state} onUpdate={update} onNavigate={navigate} />}
          {view === 'first_paper_trade' && <FirstPaperTrade state={state} onUpdate={update} onNavigate={navigate} />}
          {view === '0dte_sim'      && <DteSimView onBack={() => navigate('dashboard')} state={state} onNavigate={navigate} />}
          {view === 'meditations'   && <MeditationsView onBack={() => navigate('innerwork')} />}
          {view === 'config'        && <ConfigView state={state} onUpdate={update} onNavigate={navigate} />}
          {view === 'calculators'    && <CalculatorsLandingView state={state} onNavigate={navigate} onBack={() => navigate('dashboard')} />}
          {view === 'calc_position'  && <CalcPositionView state={state} onBack={() => navigate('calculators')} />}
          {view === 'calc_options'   && <CalcOptionsProfitView onBack={() => navigate('calculators')} />}
          {view === 'calc_breakeven' && <CalcBreakevenView onBack={() => navigate('calculators')} />}
          {view === 'calc_rr'        && <CalcRRView onBack={() => navigate('calculators')} />}
          {view === 'calc_compound'  && <CalcCompoundView state={state} onBack={() => navigate('calculators')} />}
          {Object.keys(LOCKED_CALCS).includes(view) && <LockedCalcView viewKey={view} onBack={() => navigate('calculators')} />}
        </div>
      </div>
    </AppErrorBoundary>
  );
}
